import * as React from 'react';
import { useDispatch } from 'react-redux';
import {
  Logging,
  Notification,
  API,
  NotificationData,
  ResponseError,
  Certificate,
  CertificateStatus,
  ContactCollection,
  CustomerCertificate,
  Contact,
  getUuidV4,
  getUuidFromLocationHeader,
  BusyDialog,
  SettingsContextObject,
  settingApprovalStartDateOverride,
  stringToDate,
  ConfirmationModal,
  toBoolean,
  Coverage,
  CertificateUpdate,
  getCurrentDateOnly,
  formatDate,
  DateFormatEnum,
} from '@vcd-ecm-ui/Common';
import { VtxModal } from '@vertexinc/vtx-ui-react-component-library';
import SideBySideViewerHeader from '../SideBySideViewerHeader/sbsViewerHeader';
import SideBySideViewerContentLeft from '../SideBySideViewerContentLeft/sbsViewerContentLeft';
import SideBySideViewerContentRight from '../SideBySideViewerContentRight/sbsViewerContentRight';
import SideBySideViewerProps from './SizeBySideViewerProps.interface';
import useCertificate from './useCertificate';
import CertificateRejectionViewer from '../CertificateRejectionViewer/CertificateRejectionViewer';
import '../sidebyside-viewer.scss';

const customStyles = {
  padding: '0px',
  margin: '0px',
  border: '0px solid black',
  borderRadius: '0px',
  backgroundColor: 'unset',
  backdropFilter: 'blur(4px)',
};

const maskStyle = {
  backgroundColor: 'unset',
};

const style = {
  maxWidth: '100%',
  maxHeight: '100%',
  minHeight: '100%',
  padding: '0px',
  margin: '0px',
};

const SideBySideViewer = ({
  isOpen,
  onApproved = null,
  onRejected = null,
  onEdited = null,
  onClose,
  certificateId,
  certificateUuid,
  gatewayEndPoint,
}: SideBySideViewerProps): JSX.Element => {
  const [editable, setEditable] = React.useState(false);
  const [certificateRejectionViewerOpen, setCertificateRejectionViewerOpen] = React.useState(false);
  const [notification, setNotification] = React.useState(null);
  const [customerContacts, setCustomerContacts] = React.useState(null);
  const [certificateLoadKey, setCertificateLoadKey] = React.useState(getUuidV4());
  const [selectedContact, setSelectedContact] = React.useState(null);
  const [edited, setEdited] = React.useState(false);
  const { settingsContext, setSettingsContext } = React.useContext(SettingsContextObject);
  const [approvalWarningMessage, setApprovalWarningMessage] = React.useState(null);
  const [showApprovalWarningMessage, setShowApprovalWarningMessage] = React.useState(false);
  const [editedCertificate, setEditedCertificate] = React.useState(null);

  const dispatch = useDispatch();

  // Get the Certificate - The Certificate Key changes each time the Certificate is loaded.
  // Used to re-initialize the Left Viewer State after a Certificate Update
  const [certificate, childCustomers, certificateNotification, certificateKey] = useCertificate(
    gatewayEndPoint,
    certificateUuid,
    certificateId,
    isOpen,
    certificateLoadKey, // Each time the setCertificateLoadKey changes, the certificate will be reloaded
  );

  // Make the API Call to Read the Customer Contacts
  const getCustomerContacts = (customer: CustomerCertificate, selectedContactUuid: string) => async (): Promise<void> => {
    try {
      const api = new API(gatewayEndPoint);

      // Get the Customer Contacts
      const contactCollection: ContactCollection = await api.customer.getCustomerContactsV41(customer.uuid);

      // Set the Initial Customer Contacts
      setCustomerContacts(contactCollection.contacts);

      // Set the Selected Contact
      setSelectedContact(selectedContactUuid);

      Logging.Debug('Get Contacts:', customer.name, customer.uuid, contactCollection.contacts);

      setEditable(true);
    } catch (error) {
      const notificationData: NotificationData = {
        type: 'Error',
        title: 'Contacts Read Error',
        description: `Unable to Read Contacts for '${customer.name}'`,
        error: error as ResponseError,
      };

      setNotification(notificationData);
    }
  };

  // Make the API Call to Update the Certificate
  const updateCertificate = (updates: CertificateUpdate[]) => async (): Promise<void> => {
    try {
      const api = new API(gatewayEndPoint);

      // Update the Certificate
      await api.certificate.updateCertificateV41(certificateUuid, updates);

      // Close the Editor
      setEditable(false);

      // Trigger a Reload of the Certificate
      setCertificateLoadKey(getUuidV4());

      setEdited(true);
    } catch (error) {
      const responseError: ResponseError = error as ResponseError;

      const notificationData: NotificationData = {
        type: 'Error',
        title: 'Certificate Update Error',
        description: 'Unable to Update Certificate.',
        error: error as ResponseError,
        appendMessagesForStatusCodes: [409],
        hideStatusCodes: [409],
      };

      setNotification(notificationData);
    }
  };

  // Close the Side By Side Viewer
  const onCloseSideBySideViewer = (): void => {
    Logging.Info('Side By Side Viewer: Close');

    // Inform the Parent that the Certificate was Edited
    if (edited && onEdited) {
      onEdited(certificate);
    }

    setEdited(false);
    setNotification(null);
    onClose();
  };

  // Close the Notification
  const onNotificationClose = (): void => {
    Logging.Info('Side By Side Viewer: Notification Close');
    setNotification(null);
  };

  // Make the API Call to Accept the Certificate
  const approveCertificate = () => async (): Promise<void> => {
    try {
      const api = new API(gatewayEndPoint);

      await api.certificate.approveUserCertificateV41(certificate.uuid);

      // Inform the Parent that the Certificate was Approved
      onApproved(certificate);
    } catch (error) {
      const responseError: ResponseError = error as ResponseError;

      const notificationData: NotificationData = {
        type: 'Error',
        title: 'Certificate Approval Error',
        description: `Unable to approve Certificate '${certificateId}'.`,
        error: error as ResponseError,
        appendMessagesForStatusCodes: [409],
        hideStatusCodes: [409],
      };
      setNotification(notificationData);
    }
  };

  const onOKConfirmation = (): void => {
    Logging.Info('OK Confirmation');

    setShowApprovalWarningMessage(false);

    // Dispatch the Asynchronous Event
    dispatch(approveCertificate());
  };

  const getEarliestDate = (dates: string[]) : Date => {
    let earliestDate: Date = null;

    dates.forEach((dateString: string) => {
      if (dateString) {
        const date: Date = stringToDate(dateString);

        if (!earliestDate || (date < earliestDate)) {
          earliestDate = date;
        }
      }
    });

    return earliestDate;
  };

  const getLatestDate = (dates: string[]) : Date => {
    let latestDate: Date = null;

    dates.forEach((dateString: string) => {
      if (dateString) {
        const date: Date = stringToDate(dateString);

        if (!latestDate || (date > latestDate)) {
          latestDate = date;
        }
      }
    });

    return latestDate;
  };

  const getEarliestEndExpirationDate = (): Date => {
    // Create an Array of Expiration Dates from Coverage Expiration Dates
    const expirationDates: string[] = certificate.coverages ? certificate.coverages.map((coverage: Coverage) => coverage.expirationDate) : [];

    // Get the Latest Expiration Date
    const expirationDate: Date = getLatestDate(expirationDates);

    // Get the End Date
    const endDate: Date = stringToDate(certificate.endDate);

    let earliestDate: Date = null;

    // Both End Date and Expiration Date are valid
    if (endDate && expirationDate) {
      // Get the Earliest Date from the End Date and Expiration Date
      earliestDate = (endDate < expirationDate) ? endDate : expirationDate;
    } else {
      // Use whichever of the 2 dates are valid
      earliestDate = endDate || expirationDate;
    }

    return earliestDate;
  };

  // Approve Button was Pressed in Header
  const onApprove = (): void => {
    if (settingsContext && settingsContext.certificates) {
      const approvalStartDateOverrideSetting = `${settingApprovalStartDateOverride}1`;
      const startDateOverride: boolean = toBoolean(settingsContext.certificates[approvalStartDateOverrideSetting]);

      Logging.Info('Side By Side Viewer: Approve', startDateOverride);

      if (startDateOverride === true) {
        let warningMessage = null;
        const currentDate = getCurrentDateOnly();

        if (certificate.effectiveDate) {
          const startDate: Date = stringToDate(certificate.effectiveDate);

          Logging.Debug('Start Date:', currentDate, startDate);

          if (currentDate < startDate) {
            warningMessage = 'You are approving a certificate before the initial start date. The start date will remain the same.';
          }
        }

        // Create an Array of Expiration Dates from Coverage Expiration Dates
        const expirationDates: string[] = certificate.coverages ? certificate.coverages.map((coverage: Coverage) => coverage.expirationDate) : [];

        // Get the Earliest Expiration Date
        const expirationDate: Date = getEarliestDate(expirationDates);

        // Get the End Date
        const endDate: Date = stringToDate(certificate.endDate);

        let earliestDate: Date = null;

        // Both End Date and Expiration Date are valid
        if (endDate && expirationDate) {
          // Get the Earliest Date from the End Date and Expiration Date
          earliestDate = (endDate < expirationDate) ? endDate : expirationDate;
        } else {
          // Use whichever of the 2 dates are valid
          earliestDate = endDate || expirationDate;
        }

        if (!warningMessage && earliestDate) {
          Logging.Debug('End/Expiration Date:', currentDate, earliestDate, endDate, expirationDate, expirationDates);

          if (currentDate > earliestDate) {
            if ((currentDate > endDate) && (currentDate < expirationDate)) {
              warningMessage = 'You are approving a certificate after the end date has passed. The start date will remain the same.';
            } else {
              warningMessage = 'You are approving a certificate after the expiration date has passed. The start date will remain the same.';
            }
          }
        }

        if (warningMessage) {
          Logging.Info('Approval Warning:', warningMessage);

          setApprovalWarningMessage(warningMessage);

          // Show the Warning
          setShowApprovalWarningMessage(true);
        } else {
          // Dispatch the Asynchronous Event
          dispatch(approveCertificate());
        }
      } else {
        // Dispatch the Asynchronous Event
        dispatch(approveCertificate());
      }
    } else {
      Logging.Error('Cannot get Certificate Settings');
    }
  };

  // Reject Button was Pressed in Header
  const onReject = (): void => {
    Logging.Info('Side By Side Viewer: Reject');

    // Show the Certificate Rejection Viewer
    setCertificateRejectionViewerOpen(true);
  };

  // Certificate was Rejected by the Reject Certificate Viewer
  const onCertificateRejected = (certificates: Certificate | Certificate[]): void => {
    Logging.Debug('Side By Side Viewer: Rejected');

    // Close the Rejection Viewer
    setCertificateRejectionViewerOpen(false);

    // Inform the Parent that the Certificate was Rejected
    onRejected(certificates as Certificate);
  };

  // Cancel the Rejection Viewer
  const onCancelRejectionViewer = () => {
    // Close the Rejection Viewer
    setCertificateRejectionViewerOpen(false);
  };

  // On Edit Certificate
  const onEditCertificate = () => {
    Logging.Info('On Edit Certificate');

    // Clear the Edited Certitifcate
    setEditedCertificate(null);

    // Get the Customer Contacts
    if (certificate.customer) {
      dispatch((getCustomerContacts(certificate.customer, certificate?.contact?.uuid)));
    }
  };

  // Cancel the Edit
  const onEditCancel = () => {
    Logging.Info('Edit Cancel');

    setEditable(false);

    // Trigger a Reload of the Certificate
    setCertificateLoadKey(getUuidV4());
  };

  // Save the Edit
  const onEditSave = () => {
    const updates: CertificateUpdate[] = [];

    // Contact was Updated
    if (selectedContact !== certificate.contact?.uuid) {
      updates.push({ op: 'replace', path: '/contact/uuid', value: selectedContact });
    }

    // End Date was Updated
    if ((editedCertificate != null) && (editedCertificate?.endDate !== certificate.endDate)) {
      // Convert the Date into MM/DD/YYYY format
      const endDate = formatDate(stringToDate(editedCertificate?.endDate), { dateFormat: DateFormatEnum.MDY, dateSeperator: '/' });
      updates.push({ op: 'replace', path: '/endDate', value: endDate });
    }

    if (updates.length > 0) {
      dispatch((updateCertificate(updates)));
    }
  };

  // Make the API Call to Add the Customer Contact
  const addCustomerContact = (customerUuid: string, contact: Contact) => async (): Promise<void> => {
    try {
      const api = new API(gatewayEndPoint);

      // Add the Customer
      const headers: Headers = await api.customer.addCustomerContactV41(customerUuid, contact);
      const newContactUuid: string = getUuidFromLocationHeader(headers);

      Logging.Info('Add Contact', newContactUuid, contact);

      // Reload the Contacts
      dispatch((getCustomerContacts(certificate.customer, newContactUuid)));

      setEdited(true);
    } catch (error) {
      const notificationData: NotificationData = {
        type: 'Error',
        title: 'Contact Save Error',
        description: `Unable to Save Contact '${contact.name}'.`,
        error: error as ResponseError,
      };

      setNotification(notificationData);
    }
  };

  // Save the Contact
  const onSaveContact = (contact: Contact): void => {
    // Add the Contact
    dispatch((addCustomerContact(certificate.customer.uuid, contact)));
  };

  // Not Open
  if (!isOpen) {
    return (<></>);
  }

  // Error has occured, show it
  if (certificateNotification) {
    return (
      <Notification
        data={certificateNotification}
        onNotificationClose={onCloseSideBySideViewer}
        autoClose
      />
    );
  }

  // No Valid Certificate - Still busy loading
  if (!certificate) {
    return (
      <BusyDialog
        busyMessage="Loading the Certificate ..."
        isOpen={isOpen}
        onClose={onCloseSideBySideViewer}
      />
    );
  }

  const getCertificateStatus = (): CertificateStatus => {
    let status: CertificateStatus = certificate.status && certificate.status.state ? certificate.status.state : CertificateStatus.Pending;

    // If Approved Status break it down into Active or Inactive
    if (status === CertificateStatus.Approved) {
      const earliestDate: Date = getEarliestEndExpirationDate();

      // Earliest Date Exist
      if (earliestDate) {
        const currentDate = getCurrentDateOnly();

        // If Today's Date is smaller or equal than the Expiration/End date the Certificate is Active, otherwise it is Inactive
        status = (currentDate <= earliestDate) ? CertificateStatus.Active : CertificateStatus.Inactive;
      } else {
        status = CertificateStatus.Active;
      }
    }

    return status;
  };

  // Enable the Save button if a different Contact is selected or if a Contact was added.
  // Enable the Save button if Certificate was edited
  const saveEnabled = (selectedContact !== certificate.contact?.uuid)
    || ((editedCertificate != null) && (editedCertificate?.endDate !== certificate.endDate));

  return (
    <div>
      <VtxModal
        visible={isOpen}
        onCancel={onCloseSideBySideViewer}
        bodyStyle={customStyles}
        maskStyle={maskStyle}
        style={style}
        width="100%"
        footer={null}
        closable={false}
      >
        <div className="sbsContainer">
          <SideBySideViewerHeader
            gatewayEndPoint={gatewayEndPoint}
            id={certificate.exemptionId}
            createdDateTime={certificate.createdDateTime}
            certificate={certificate}
            status={getCertificateStatus()}
            isOpen={isOpen}
            editable={editable}
            onEditCertificate={onEditCertificate}
            onApprove={onApprove}
            onReject={onReject}
            onClose={onCloseSideBySideViewer}
            onEditCancel={onEditCancel}
            onEditSave={onEditSave}
            saveEnabled={saveEnabled}
          />
          <div className="sbsContent">
            <SideBySideViewerContentLeft
              key={certificateKey}
              gatewayEndPoint={gatewayEndPoint}
              certificate={certificate}
              childCustomers={childCustomers}
              editable={editable}
              customerContacts={customerContacts}
              selectedContact={selectedContact}
              setSelectedContact={setSelectedContact}
              onSaveContact={onSaveContact}
              setEditedCertificate={setEditedCertificate}
            />
            <SideBySideViewerContentRight certificate={certificate} editable={editable} />
          </div>
        </div>
        {
          certificateRejectionViewerOpen
          && (
            <CertificateRejectionViewer
              gatewayEndPoint={gatewayEndPoint}
              isOpen={certificateRejectionViewerOpen}
              certificateUuid={certificate.uuid}
              certificateId={certificate.exemptionId}
              certificate={certificate}
              onRejected={onCertificateRejected}
              onCancel={onCancelRejectionViewer}
            />
          )
        }
        <Notification
          data={notification}
          onNotificationClose={onNotificationClose}
          autoClose
        />
        <ConfirmationModal
          isOpen={showApprovalWarningMessage}
          heading="Approval Warning"
          message={approvalWarningMessage}
          onOK={onOKConfirmation}
          confirmationType="Warning"
        />
      </VtxModal>
    </div>
  );
};

export default SideBySideViewer;
